import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { buildEngagementFilter, filterAudienceEvents } from '../utils/emailBotDetection';
import { useAuth } from '../components/auth/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useEmailCampaignData } from '../hooks/useEmailCampaignData';
import { canViewAllEmail } from '../utils/emailAccess';
import { isAssignedCustomer } from '../utils/reportOwnership';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EmailMarketingAnalyticsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const viewAllEmail = canViewAllEmail(currentUser, can);
  const { campaigns, events: rawEvents, loading, error } = useEmailCampaignData(currentUser, viewAllEmail);
  const events = useMemo(() => rawEvents.map(event => ({
    ...event,
    timestamp: event.timestamp?.toDate ? event.timestamp.toDate() : new Date(event.timestamp),
  })), [rawEvents]);
  const [customers, setCustomers] = useState([]);
  const [customerError, setCustomerError] = useState(null);
  const [timeRange, setTimeRange] = useState('all'); // all, 7d, 30d, 90d

  useEffect(() => {
    let active = true;
    setCustomers([]);
    setCustomerError(null);
    // Load customers for customer name lookup
    const customersUnsub = onSnapshot(
      query(collection(db, collectionPath('customers'))),
      (snap) => {
        if (!active) return;
        const customersData = snap.docs.map(doc => {
          const data = doc.data();
          return { 
            firestoreId: doc.id,  // The Firestore document ID
            ...data
          };
        });
        setCustomers(viewAllEmail ? customersData : customersData.filter(customer => isAssignedCustomer(customer, currentUser)));
      },
      error => {
        if (!active) return;
        console.error('Failed to load email analytics customers:', error);
        setCustomers([]);
        setCustomerError(error);
      }
    );

    return () => {
      active = false;
      customersUnsub();
    };
  }, [currentUser, viewAllEmail]);

  // Calculate aggregate analytics
  const analytics = useMemo(() => {
    // BCC/verification copies (fromEmail, replyTo, campaignBccEmails setting) are internal
    // addresses, not real audience members — exclude from every aggregate stat.
    const audienceEvents = filterAudienceEvents(events);

    // Filter by time range
    let filteredEvents = audienceEvents;
    if (timeRange !== 'all') {
      const days = parseInt(timeRange.replace('d', ''));
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      filteredEvents = audienceEvents.filter(e => e.timestamp >= cutoff);
    }

    // Calculate overall stats (with bot filtering)
    const { isHumanEngagement } = buildEngagementFilter(filteredEvents);
    
    const uniqueOpensSet = new Set();
    const uniqueClicksSet = new Set();
    const uniqueRecipientsSet = new Set();
    let sent = 0;
    let delivered = 0;
    let totalOpens = 0;
    let totalClicks = 0;
    let bounces = 0;
    let unsubscribes = 0;

    filteredEvents.forEach(event => {
      if (event.recipientEmail) uniqueRecipientsSet.add(event.recipientEmail);
      if (event.eventType === 'sent') sent++;
      if (event.eventType === 'delivered') delivered++;
      if (event.eventType === 'opened' && isHumanEngagement(event)) {
        totalOpens++;
        if (event.recipientEmail) uniqueOpensSet.add(event.recipientEmail);
      }
      if (event.eventType === 'clicked' && isHumanEngagement(event)) {
        totalClicks++;
        if (event.recipientEmail) uniqueClicksSet.add(event.recipientEmail);
      }
      if (event.eventType === 'bounced') bounces++;
      if (event.eventType === 'unsubscribed') unsubscribes++;
    });

    const stats = {
      sent,
      delivered,
      opens: totalOpens,
      uniqueOpens: uniqueOpensSet.size,
      clicks: totalClicks,
      uniqueClicks: uniqueClicksSet.size,
      bounces,
      unsubscribes,
      uniqueRecipients: uniqueRecipientsSet.size
    };

    // Timeline data (grouped by date) — only count human engagement for opens/clicks
    const timeline = {};
    filteredEvents.forEach(event => {
      const date = event.timestamp.toISOString().split('T')[0];
      if (!timeline[date]) {
        timeline[date] = { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 };
      }
      if (event.eventType === 'sent') timeline[date].sent++;
      if (event.eventType === 'delivered') timeline[date].delivered++;
      if (event.eventType === 'opened' && isHumanEngagement(event)) timeline[date].opened++;
      if (event.eventType === 'clicked' && isHumanEngagement(event)) timeline[date].clicked++;
      if (event.eventType === 'bounced') timeline[date].bounced++;
    });

    // Campaign performance
    const campaignPerformance = {};
    campaigns.forEach(campaign => {
      const campaignEvents = filteredEvents.filter(e => e.campaignId === campaign.id);
      const uniqueOpens = new Set();
      const uniqueClicks = new Set();
      let campaignSent = 0;

      // Use per-campaign bot detection
      const { isHumanEngagement: isCampaignHuman } = buildEngagementFilter(campaignEvents);
      campaignEvents.forEach(event => {
        if (event.eventType === 'sent') campaignSent++;
        if (event.eventType === 'opened' && event.recipientEmail && isCampaignHuman(event)) uniqueOpens.add(event.recipientEmail);
        if (event.eventType === 'clicked' && event.recipientEmail && isCampaignHuman(event)) uniqueClicks.add(event.recipientEmail);
      });

      if (campaignSent > 0) {
        campaignPerformance[campaign.id] = {
          name: campaign.name,
          sent: campaignSent,
          openRate: (uniqueOpens.size / campaignSent) * 100,
          clickRate: (uniqueClicks.size / campaignSent) * 100
        };
      }
    });

    // Top performing campaigns
    const topCampaigns = Object.values(campaignPerformance)
      .sort((a, b) => b.openRate - a.openRate)
      .slice(0, 10);

    // Opens by hour — human engagement only
    const opensByHour = Array(24).fill(0);
    filteredEvents
      .filter(e => e.eventType === 'opened' && isHumanEngagement(e))
      .forEach(event => {
        opensByHour[event.timestamp.getHours()]++;
      });

    // Device breakdown — human engagement only
    const devices = { mobile: 0, desktop: 0, tablet: 0, unknown: 0 };
    filteredEvents
      .filter(e => e.eventType === 'opened' && e.metadata?.userAgent && isHumanEngagement(e))
      .forEach(event => {
        const ua = event.metadata.userAgent.toLowerCase();
        if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
          devices.mobile++;
        } else if (ua.includes('tablet') || ua.includes('ipad')) {
          devices.tablet++;
        } else if (ua.includes('mozilla') || ua.includes('chrome') || ua.includes('safari')) {
          devices.desktop++;
        } else {
          devices.unknown++;
        }
      });

    // Top clicked links across all campaigns — human clicks only
    const links = {};
    filteredEvents
      .filter(e => e.eventType === 'clicked' && e.metadata?.clickedUrl && isHumanEngagement(e))
      .forEach(event => {
        const url = event.metadata.clickedUrl;
        links[url] = (links[url] || 0) + 1;
      });

    const topLinks = Object.entries(links)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([url, clicks]) => ({ url, clicks }));

    // Top 10 Customers by Engagement (opens + clicks)
    const customerEngagement = {};
    filteredEvents.forEach(event => {
      const customerId = event.recipientCustomerId;
      if (!customerId) return;
      
      if (!customerEngagement[customerId]) {
        customerEngagement[customerId] = {
          customerId,
          customerName: 'Unknown Customer',
          opens: 0,
          clicks: 0,
          totalEngagement: 0
        };
      }
      
      if (event.eventType === 'opened' && isHumanEngagement(event)) {
        customerEngagement[customerId].opens++;
        customerEngagement[customerId].totalEngagement++;
      }
      if (event.eventType === 'clicked' && isHumanEngagement(event)) {
        customerEngagement[customerId].clicks++;
        customerEngagement[customerId].totalEngagement++;
      }
    });

    const topCustomers = Object.values(customerEngagement)
      .sort((a, b) => b.totalEngagement - a.totalEngagement)
      .slice(0, 10);
    
    // Resolve customer names after sorting
    topCustomers.forEach(tc => {
      // Try multiple matching strategies
      const customer = customers.find(c => 
        c.firestoreId === tc.customerId ||  // Match by Firestore document ID
        c.customerId === tc.customerId ||    // Match by customerId field
        c.id === tc.customerId ||           // Match by id field
        c._oldDocId === tc.customerId       // Match by _oldDocId field
      );
      if (customer) {
        tc.customerName = customer.customerName || 'Unknown Customer';
      }
    });

    // Top 10 Email Addresses by Engagement (opens + clicks)
    const emailEngagement = {};
    filteredEvents.forEach(event => {
      const email = event.recipientEmail;
      if (!email) return;
      
      if (!emailEngagement[email]) {
        emailEngagement[email] = {
          email,
          customerId: event.recipientCustomerId,
          customerName: 'Unknown',
          opens: 0,
          clicks: 0,
          totalEngagement: 0
        };
      }
      
      if (event.eventType === 'opened' && isHumanEngagement(event)) {
        emailEngagement[email].opens++;
        emailEngagement[email].totalEngagement++;
      }
      if (event.eventType === 'clicked' && isHumanEngagement(event)) {
        emailEngagement[email].clicks++;
        emailEngagement[email].totalEngagement++;
      }
    });

    const topEmails = Object.values(emailEngagement)
      .sort((a, b) => b.totalEngagement - a.totalEngagement)
      .slice(0, 10);
    
    // Resolve customer names after sorting
    topEmails.forEach(te => {
      if (te.customerId) {
        // Try multiple matching strategies
        const customer = customers.find(c => 
          c.firestoreId === te.customerId ||  // Match by Firestore document ID
          c.customerId === te.customerId ||    // Match by customerId field
          c.id === te.customerId ||           // Match by id field
          c._oldDocId === te.customerId       // Match by _oldDocId field
        );
        if (customer) {
          te.customerName = customer.customerName || 'Unknown';
        }
      }
    });

    return {
      stats,
      timeline,
      topCampaigns,
      opensByHour,
      devices,
      topLinks,
      topCustomers,
      topEmails
    };
  }, [campaigns, events, customers, timeRange]);

  if (loading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading analytics...</div>
        </div>
      </main>
    );
  }

  const stats = analytics.stats;
  const openRate = stats.sent > 0 ? ((stats.uniqueOpens / stats.sent) * 100).toFixed(1) : 0;
  const clickRate = stats.sent > 0 ? ((stats.uniqueClicks / stats.sent) * 100).toFixed(1) : 0;
  const bounceRate = stats.sent > 0 ? ((stats.bounces / stats.sent) * 100).toFixed(1) : 0;
  const deliveryRate = stats.sent > 0 ? ((stats.delivered / stats.sent) * 100).toFixed(1) : 0;

  // Prepare chart data
  const timelineData = {
    labels: Object.keys(analytics.timeline).sort(),
    datasets: [
      {
        label: 'Sent',
        data: Object.keys(analytics.timeline).sort().map(date => analytics.timeline[date].sent),
        borderColor: 'rgb(107, 114, 128)',
        backgroundColor: 'rgba(107, 114, 128, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Opens',
        data: Object.keys(analytics.timeline).sort().map(date => analytics.timeline[date].opened),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Clicks',
        data: Object.keys(analytics.timeline).sort().map(date => analytics.timeline[date].clicked),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  const hourlyData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: 'Opens by Hour',
        data: analytics.opensByHour,
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1
      }
    ]
  };

  const deviceData = {
    labels: ['Desktop', 'Mobile', 'Tablet', 'Unknown'],
    datasets: [
      {
        data: [
          analytics.devices.desktop,
          analytics.devices.mobile,
          analytics.devices.tablet,
          analytics.devices.unknown
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(107, 114, 128, 0.7)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)',
          'rgb(245, 158, 11)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 1
      }
    ]
  };

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      {(error || customerError) && <p role="alert" className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700">Could not load email analytics: {(error || customerError).message}</p>}
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Email Marketing Analytics</h2>
            <p className="text-gray-600 mt-1">Overall performance across all campaigns</p>
          </div>

          {/* Time Range Filter */}
          <div className="flex space-x-2">
            {['all', '7d', '30d', '90d'].map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded ${
                  timeRange === range
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {range === 'all' ? 'All Time' : `Last ${range.replace('d', '')} Days`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Total Campaigns</div>
          <div className="text-2xl font-bold text-gray-900">{campaigns.length}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Emails Sent</div>
          <div className="text-2xl font-bold text-gray-900">{stats.sent.toLocaleString()}</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: 95-98%">
            Delivered
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-green-600">{stats.delivered.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{deliveryRate}% rate</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: 20-25%">
            Unique Opens
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">{stats.uniqueOpens.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{openRate}% rate</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: 2-5%">
            Unique Clicks
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-indigo-600">{stats.uniqueClicks.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{clickRate}% rate</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: <2%">
            Bounces
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-red-600">{stats.bounces.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">{bounceRate}% rate</div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: <0.5%">
            Unsubscribes
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold text-orange-600">{stats.unsubscribes.toLocaleString()}</div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Timeline Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Over Time</h3>
          <Line
            data={timelineData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { position: 'top' },
                tooltip: { mode: 'index', intersect: false }
              },
              scales: {
                y: { beginAtZero: true }
              }
            }}
          />
        </div>

        {/* Device Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Breakdown</h3>
          <div className="flex items-center justify-center">
            <div style={{ maxWidth: '300px', maxHeight: '300px' }}>
              <Doughnut
                data={deviceData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Opens by Hour */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Opens by Hour of Day</h3>
          <Bar
            data={hourlyData}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              plugins: {
                legend: { display: false }
              },
              scales: {
                y: { beginAtZero: true }
              }
            }}
          />
        </div>

        {/* Top Campaigns */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Campaigns</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {analytics.topCampaigns && analytics.topCampaigns.length > 0 ? (
              analytics.topCampaigns.map((campaign, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-grow mr-4">
                    <div className="text-sm font-medium text-gray-900 truncate">{campaign.name}</div>
                    <div className="text-xs text-gray-500">{campaign.sent} sent</div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-blue-600">{campaign.openRate.toFixed(1)}%</div>
                      <div className="text-xs text-gray-500">opens</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-indigo-600">{campaign.clickRate.toFixed(1)}%</div>
                      <div className="text-xs text-gray-500">clicks</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No campaign data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Links */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Clicked Links Across All Campaigns</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analytics.topLinks && analytics.topLinks.length > 0 ? (
            analytics.topLinks.map((link, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b">
                <div className="flex-grow mr-4">
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-700 truncate block"
                    title={link.url}
                  >
                    {link.url.length > 60 ? link.url.substring(0, 60) + '...' : link.url}
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-semibold text-gray-900">{link.clicks}</span>
                  <span className="text-xs text-gray-500">clicks</span>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-2 text-center py-8 text-gray-500">
              No link clicks recorded yet
            </div>
          )}
        </div>
      </div>

      {/* Top Engaged Customers and Email Addresses Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top 10 Customers by Engagement */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Most Engaged Customers</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {analytics.topCustomers && analytics.topCustomers.length > 0 ? (
              analytics.topCustomers.map((customer, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center space-x-3 flex-grow mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-grow">
                      <div className="text-sm font-medium truncate">
                        {customer.customerId ? (
                          <button onClick={() => navigate(`/customer/${customer.customerId}`)} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">{customer.customerName}</button>
                        ) : (
                          <span className="text-gray-900">{customer.customerName}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {customer.opens} opens • {customer.clicks} clicks
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right">
                      <div className="text-lg font-bold text-indigo-600">{customer.totalEngagement}</div>
                      <div className="text-xs text-gray-500">interactions</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No customer engagement data yet
              </div>
            )}
          </div>
        </div>

        {/* Top 10 Email Addresses by Engagement */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Most Engaged Email Addresses</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {analytics.topEmails && analytics.topEmails.length > 0 ? (
              analytics.topEmails.map((emailData, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="flex items-center space-x-3 flex-grow mr-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="text-sm font-medium truncate" title={emailData.email}>
                        {emailData.customerId ? (
                          <button onClick={() => navigate(`/customer/${emailData.customerId}`)} className="text-indigo-600 hover:text-indigo-800 hover:underline text-left">{emailData.email}</button>
                        ) : (
                          <span className="text-gray-900">{emailData.email}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate" title={emailData.customerName}>
                        {emailData.customerId ? (
                          <button onClick={() => navigate(`/customer/${emailData.customerId}`)} className="hover:text-indigo-600 hover:underline text-left">{emailData.customerName}</button>
                        ) : emailData.customerName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {emailData.opens} opens • {emailData.clicks} clicks
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">{emailData.totalEngagement}</div>
                      <div className="text-xs text-gray-500">interactions</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No email engagement data yet
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default EmailMarketingAnalyticsPage;
